import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchTiers, fetchActiveUsers, sendNewsletter, previewNewsletter } from '../services/adminApi';

export default function AdminNewsletterPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);

  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterEmoji, setNewsletterEmoji] = useState('📰');
  const [newsletterBody, setNewsletterBody] = useState('');
  const [sendNewsletterLoading, setSendNewsletterLoading] = useState(false);
  const [sendNewsletterMessage, setSendNewsletterMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const newsletterBodyRef = useRef(null);

  const [newsletterRecipientType, setNewsletterRecipientType] = useState('all');
  const [newsletterSelectedUsernames, setNewsletterSelectedUsernames] = useState('');
  const [newsletterSelectedTierIds, setNewsletterSelectedTierIds] = useState([]);

  const [availableUsers, setAvailableUsers] = useState([]);
  const [usernameSearchInput, setUsernameSearchInput] = useState('');
  const [showUsernameDropdown, setShowUsernameDropdown] = useState(false);
  const [highlightedUsernameIndex, setHighlightedUsernameIndex] = useState(-1);
  const usernameInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [tiersRes, usersRes] = await Promise.all([fetchTiers(), fetchActiveUsers()]);
        /* c8 ignore start */
        if (!mounted) return;
        setTiers(Array.isArray(tiersRes) ? tiersRes : (tiersRes.tiers || []));
        const usersList = Array.isArray(usersRes) ? usersRes : [];
        const normalizedUsers = usersList
          .map(u => (typeof u === 'string' ? u : (u.username ?? u.login ?? '')))
        /* c8 ignore stop */
          .filter(Boolean);
        setAvailableUsers(normalizedUsers);
        
        // Pre-fill username if provided in query parameters
        const usernameParam = searchParams.get('username');
        if (usernameParam) {
          setUsernameSearchInput(decodeURIComponent(usernameParam));
          setNewsletterRecipientType('usernames');
        }
      } catch (e) {
        // swallow — noncritical for this UI
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [searchParams]);

  function applyFormat(type) {
    const textarea = newsletterBodyRef.current;
    /* c8 ignore start */
    if (!textarea) return;
    /* c8 ignore stop */
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = newsletterBody.slice(0, start);
    const selected = newsletterBody.slice(start, end);
    const after = newsletterBody.slice(end);

    let replacement = '';
    if (type === 'bold') {
      replacement = `**${selected || 'bold text'}**`;
    } else if (type === 'italic') {
      replacement = `*${selected || 'italic text'}*`;
    } else if (type === 'ul') {
      if (selected) {
        replacement = selected.split(/\r?\n/).map(l => (l.trim() ? `- ${l}` : '')).join('\n');
      } else {
        replacement = `- List item 1\n- List item 2`;
      }
    } else if (type === 'table') {
      replacement = `| Column 1 | Column 2 |\n| --- | --- |\n| Value 1 | Value 2 |`;
    /* c8 ignore start */
    } else if (type === 'link') {
      // Prompt for URL and optionally link text; insert a markdown link [text](url)
      const url = window.prompt('URL to link to (include http:// or https://):');
      if (!url) return;
      const linkText = selected || window.prompt('Link text (visible in email):', url) || url;
      replacement = `[${linkText}](${url})`;
    }
    /* c8 ignore stop */

    const newValue = before + replacement + after;
    setNewsletterBody(newValue);
    const cursorPos = before.length + replacement.length;
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = cursorPos;
    });
  }

  const filteredUsernames = availableUsers.filter(u => u.toLowerCase().includes(usernameSearchInput.toLowerCase())).slice(0, 10);

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Newsletter</h1>
        <p className="text-gray-400 mt-1">Compose and send newsletters to users</p>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Send to:</label>
              <select
                value={newsletterRecipientType}
                onChange={(e) => {
                  setNewsletterRecipientType(e.target.value);
                  setNewsletterSelectedUsernames('');
                  setNewsletterSelectedTierIds([]);
                  setUsernameSearchInput('');
                }}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
              >
                <option value="all">All active users</option>
                <option value="admin">Admin only</option>
                <option value="tiers">Select by tier</option>
                <option value="usernames">Select by username</option>
              </select>
            </div>

            {newsletterRecipientType === 'tiers' && (
              <div>
                <p className="text-xs text-gray-400 mb-2">Select one or more tiers:</p>
                <div className="space-y-1 max-h-32 overflow-y-auto bg-dark-900 p-2 rounded border border-dark-600">
                  {tiers.map(t => {
                    /* c8 ignore start */
                    const tierId = String(t.id ?? t._id ?? t.key);
                    /* c8 ignore stop */
                    return (
                    <label key={tierId} className="flex items-center text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={newsletterSelectedTierIds.includes(tierId)}
                        onChange={(e) => {
                          /* c8 ignore start */
                          if (e.target.checked) {
                            setNewsletterSelectedTierIds([...newsletterSelectedTierIds, tierId]);
                          } else {
                            setNewsletterSelectedTierIds(newsletterSelectedTierIds.filter(id => id !== tierId));
                          }
                          /* c8 ignore stop */
                        }}
                        className="mr-2"
                      />
                      {/* c8 ignore start */}
                      {t.name || t.title || tierId}
                      {/* c8 ignore stop */}
                    </label>
                    );
                  })}
                </div>
              </div>
            )}

            {newsletterRecipientType === 'usernames' && (
              <div>
                <label className="block text-xs text-gray-300 mb-1">Select usernames:</label>
                <div className="relative">
                  <input
                    ref={usernameInputRef}
                    type="text"
                    value={usernameSearchInput}
                    onChange={(e) => {
                      setUsernameSearchInput(e.target.value);
                      setShowUsernameDropdown(true);
                      setHighlightedUsernameIndex(-1);
                    }}
                    onFocus={() => setShowUsernameDropdown(true)}
                    onBlur={() => setTimeout(() => setShowUsernameDropdown(false), 100)}
                    onKeyDown={(e) => {
                      /* c8 ignore start */
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightedUsernameIndex(prev => prev < filteredUsernames.length - 1 ? prev + 1 : prev);
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightedUsernameIndex(prev => prev > 0 ? prev - 1 : -1);
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (highlightedUsernameIndex >= 0 && highlightedUsernameIndex < filteredUsernames.length) {
                          const username = filteredUsernames[highlightedUsernameIndex];
                          const list = newsletterSelectedUsernames.split(',').map(s => s.trim()).filter(s => s.length > 0);
                          if (!list.includes(username)) list.push(username);
                          setNewsletterSelectedUsernames(list.join(', '));
                          setUsernameSearchInput('');
                          setShowUsernameDropdown(false);
                          setHighlightedUsernameIndex(-1);
                          usernameInputRef.current?.focus();
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowUsernameDropdown(false);
                        setHighlightedUsernameIndex(-1);
                      }
                      /* c8 ignore stop */
                    }}
                    placeholder="Type to search usernames..."
                    className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"
                  />
                  {showUsernameDropdown && usernameSearchInput && (
                    <div className="absolute z-10 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg max-h-40 overflow-y-auto">
                      {filteredUsernames.map((username, index) => (
                        <button
                          key={username}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const list = newsletterSelectedUsernames.split(',').map(s => s.trim()).filter(s => s.length > 0);
                            if (!list.includes(username)) list.push(username);
                            setNewsletterSelectedUsernames(list.join(', '));
                            setUsernameSearchInput('');
                            setShowUsernameDropdown(false);
                            setHighlightedUsernameIndex(-1);
                            usernameInputRef.current?.focus();
                          }}
                          className={`block w-full text-left px-3 py-2 text-sm ${index === highlightedUsernameIndex ? 'bg-dark-700 text-white' : 'hover:bg-dark-700 text-gray-300'}`}
                        >
                          {username}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {newsletterSelectedUsernames && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-400 mb-1">Selected:</p>
                    <div className="flex flex-wrap gap-1">
                      {newsletterSelectedUsernames.split(',').map(u => u.trim()).filter(u => u.length > 0).map(username => (
                        <span key={username} className="inline-flex items-center gap-1 px-2 py-1 bg-dark-700 rounded text-xs text-gray-300">
                          {username}
                          <button type="button" onClick={() => {
                            const list = newsletterSelectedUsernames.split(',').map(s => s.trim()).filter(s => s.length > 0 && s !== username);
                            setNewsletterSelectedUsernames(list.join(', '));
                          }} className="ml-1 hover:text-red-400">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <input placeholder="Subject" value={newsletterSubject} onChange={(e) => setNewsletterSubject(e.target.value)} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white" />
            <label className="sr-only">Emoji</label>
            <select value={newsletterEmoji} onChange={(e) => setNewsletterEmoji(e.target.value)} className="w-24 px-2 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white">
              <option value="📰">📰</option>
              <option value="📢">📢</option>
              <option value="🎉">🎉</option>
            </select>

            <div className="flex items-center space-x-2 mb-2">
              <button type="button" onClick={() => applyFormat('bold')} className="px-2 py-1 bg-dark-700 rounded text-white"><strong>B</strong></button>
              <button type="button" onClick={() => applyFormat('italic')} className="px-2 py-1 bg-dark-700 rounded text-white"><em>I</em></button>
              <button type="button" onClick={() => applyFormat('ul')} className="px-2 py-1 bg-dark-700 rounded text-white">• List</button>
              <button type="button" onClick={() => applyFormat('table')} className="px-2 py-1 bg-dark-700 rounded text-white">Table</button>
              <button type="button" onClick={() => applyFormat('link')} className="px-2 py-1 bg-dark-700 rounded text-white">🔗 Link</button>
            </div>

            <textarea ref={newsletterBodyRef} placeholder="Body (Markdown supported: **bold**, *italic*, - list, simple tables)" value={newsletterBody} onChange={(e) => setNewsletterBody(e.target.value)} rows={8} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white" />

            <div className="flex space-x-2">
              <button onClick={async () => {
                try {
                  const res = await previewNewsletter({ subject: newsletterSubject, emoji: newsletterEmoji, body: newsletterBody, recipientType: newsletterRecipientType, selectedUsernames: newsletterSelectedUsernames, selectedTierIds: newsletterSelectedTierIds });
                  setPreviewHtml(res?.html || '');
                  setShowPreview(true);
                } catch (e) {
                  setSendNewsletterMessage(e.message || 'Failed to generate preview');
                }
              }} disabled={sendNewsletterLoading} className="px-3 py-2 bg-gray-600 rounded-lg text-white">Preview</button>

              <button onClick={async () => {
                setSendNewsletterLoading(true);
                setSendNewsletterMessage('');
                try {
                  const res = await sendNewsletter({ subject: newsletterSubject, emoji: newsletterEmoji, body: newsletterBody, mode: 'test', recipientType: newsletterRecipientType, selectedUsernames: newsletterSelectedUsernames, selectedTierIds: newsletterSelectedTierIds });
                  setSendNewsletterMessage(res?.stats ? `Test sent: ${res.stats.sent}` : 'Test sent');
                } catch (e) {
                  setSendNewsletterMessage(e.message || 'Failed to send test');
                } finally { setSendNewsletterLoading(false); }
              }} disabled={sendNewsletterLoading} className="px-3 py-2 bg-secondary rounded-lg text-white">{sendNewsletterLoading ? 'Sending…' : 'Send Test'}</button>

              <button onClick={async () => {
                if (!confirm('Send newsletter to selected recipients? This cannot be undone.')) return;
                setSendNewsletterLoading(true);
                setSendNewsletterMessage('');
                try {
                  const res = await sendNewsletter({ subject: newsletterSubject, emoji: newsletterEmoji, body: newsletterBody, mode: 'send', recipientType: newsletterRecipientType, selectedUsernames: newsletterSelectedUsernames, selectedTierIds: newsletterSelectedTierIds });
                  setSendNewsletterMessage(res?.stats ? `Sent: ${res.stats.sent} / ${res.stats.total}` : 'Send complete');
                } catch (e) {
                  setSendNewsletterMessage(e.message || 'Failed to send');
                } finally { setSendNewsletterLoading(false); }
              }} disabled={sendNewsletterLoading} className="px-3 py-2 bg-primary rounded-lg text-white">{sendNewsletterLoading ? 'Sending…' : 'Send to Selected'}</button>
            </div>
            {sendNewsletterMessage && <p className="text-sm text-gray-300 mt-2">{sendNewsletterMessage}</p>}
          </div>
        </div>
      )}

      {showPreview && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                  <h3 className="text-xl font-bold text-white">Newsletter Preview</h3>
                  <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  {previewHtml ? (
                    <>
                      <div className="mb-3">
                        <div className="px-3 py-2 bg-dark-700 rounded-t text-sm text-gray-200 border border-dark-600">
                          <span className="mr-2">{newsletterEmoji}</span>
                          <span className="font-semibold">{newsletterSubject || '(no subject)'}</span>
                        </div>
                      </div>
                      <iframe title="newsletter-preview" srcDoc={previewHtml} className="w-full h-full border-0 rounded" style={{ minHeight: '400px' }} />
                    </>
                  ) : (
                    <p className="text-gray-400">Loading preview...</p>
                  )}
                </div>
              </div>
            </div>
          )}
    </div>
  );
}
